import React from 'react';
import { cn } from '@/utils';

/**
 * Progress for a file upload that takes long enough to look like a hang.
 *
 * The bar tracks bytes leaving the browser. Reaching 100% does not mean the
 * work is finished — the server still has to store the file and read its
 * text — so at 100% the label says so rather than claiming completion
 * (customer request, 2026-09-19: "the page just stays still and users may
 * think its stuck").
 */
export const UploadProgress: React.FC<{
  percent: number;
  fileName?: string;
  className?: string;
}> = ({ percent, fileName, className }) => {
  const pct = Math.max(0, Math.min(100, Math.round(percent)));
  const done = pct >= 100;

  return (
    <div className={cn('w-full', className)} role="status" aria-live="polite">
      <div className="flex items-center justify-between mb-1 text-xs text-gray-600 dark:text-gray-400">
        <span className="truncate pr-2">
          {done ? 'Processing on the server…' : `Uploading${fileName ? ` ${fileName}` : ''}…`}
        </span>
        <span className="tabular-nums font-medium">{done ? '' : `${pct}%`}</span>
      </div>
      <div className="h-1.5 w-full rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
        <div
          className={cn(
            'h-full rounded-full bg-blue-600 transition-[width] duration-200 ease-out',
            // At 100% the transfer is done but the server is still working:
            // keep the bar moving so it does not read as finished-and-stuck.
            done && 'animate-pulse'
          )}
          style={{ width: `${done ? 100 : pct}%` }}
        />
      </div>
      {done && (
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          Large documents are scanned for text, which can take a minute.
        </p>
      )}
    </div>
  );
};

export default UploadProgress;
